/*
 * Payment emails: pure response shaping, plain-English status explanations, the send-switch
 * wording and error routing. Unit-tested in adapters.test.js.
 */
import { EM_DASH, api_error } from '../format';
import { with_ist } from '../shared/ist';
import { reason_error } from '../needs_attention/params';

const str = (v) => (typeof v === 'string' ? v : '');
const list = (v) => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);

export const CODE_SWITCH_CHANGED = 'EMAIL_SWITCH_CHANGED';
export const CODE_BAD_QUERY = 'BAD_OUTBOX_QUERY';

// teacher_payment_email_outbox.status, in the DDL's order (payemail.Statuses); labels as the server words them.
export const OUTBOX_STATUSES = [
  { value: 'PENDING', label: 'Waiting to send' },
  { value: 'SENDING', label: 'Sending' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'SKIPPED', label: 'Not sent' },
  { value: 'UNCONFIRMED', label: 'Could not confirm' }
];

// teacher_payment_email_outbox.kind, in the DDL's order (payemail.Kinds); labels as the server words them.
export const OUTBOX_KINDS = [
  { value: 'PAYMENT_DETAILS_SUBMITTED', label: 'Payment details submitted' },
  { value: 'PAYMENT_DETAILS_APPROVED', label: 'Payment details approved' },
  { value: 'PAYMENT_DETAILS_REJECTED', label: 'Payment details rejected' },
  { value: 'INVOICE_CONFIRMED', label: 'Invoice confirmed' },
  { value: 'PAYMENT_SENT', label: 'Payment sent' },
  { value: 'INVOICE_REMINDER', label: 'Invoice reminder' },
  { value: 'PAYMENT_BREAKDOWN', label: 'Payment breakdown' }
];

const TONES = {
  PENDING: 'blue',
  SENDING: 'blue',
  SENT: 'green',
  FAILED: 'red',
  SKIPPED: 'gray',
  UNCONFIRMED: 'orange'
};

// What each status means for the teacher, in plain English (payemail/outcome.go).
export const status_help = (status, mode) => {
  switch (status) {
    case 'PENDING':
      return 'Queued. It is sent automatically, and retried automatically after a temporary problem.';
    case 'SENDING':
      return 'Being handed to the email service right now.';
    case 'SENT':
      return mode === 'TEST'
        ? 'Sent to the test inboxes only. The teacher did not get it.'
        : 'The email service accepted it for the teacher.';
    case 'FAILED':
      return 'Not sent. Every attempt failed, or a safety check stopped it.';
    case 'SKIPPED':
      return 'Never sent: the teacher could not be emailed (for example, no address or a deleted account).';
    case 'UNCONFIRMED':
      return 'It may or may not have reached the teacher. It is never resent automatically; check with the teacher first.';
    default:
      return '';
  }
};

const status_label = (status, mode, server_label) => {
  if (server_label) return server_label;
  const known = OUTBOX_STATUSES.find((s) => s.value === status);
  if (!known) return status || EM_DASH;
  return status === 'SENT' && mode === 'TEST' ? 'Sent (test)' : known.label;
};

// The API sends only payemail.DeliveryProblem's sentence, so bounce / spam is read from its text.
export const delivery_problem_kind = (text) => {
  const t = str(text).toLowerCase();
  if (!t) return '';
  if (t.includes('bounced')) return 'bounced';
  if (t.includes('spam')) return 'complaint';
  return 'other';
};

const PROBLEM_CHIP = {
  bounced: 'Bounced',
  complaint: 'Spam complaint',
  other: 'Delivery problem'
};

// One short muted line under the chip; the server sentence stays whole when it is not a known one.
const problem_reason = (kind, text) => {
  if (kind === 'bounced') {
    return /does not accept mail/i.test(text)
      ? 'Address does not accept mail'
      : 'Bounced after sending';
  }
  if (kind === 'complaint') return 'Teacher marked it as spam';
  return text;
};

// Statuses whose explanation is a warning the row note line flags.
const WARN_STATUSES = new Set(['FAILED', 'UNCONFIRMED']);

// One answer in the status track; sentences (meaning, failure reason) go to the row's note line.
export const status_display = ({
  status,
  mode,
  server_label,
  failure_reason,
  delivery_problem
}) => {
  const problem = delivery_problem_kind(delivery_problem);
  if (problem) {
    return {
      label: PROBLEM_CHIP[problem],
      tone: 'red',
      reason: problem_reason(problem, str(delivery_problem)),
      note: '',
      note_warn: false
    };
  }
  const help = status === 'SENT' && mode !== 'TEST' ? '' : status_help(status, mode);
  return {
    label: status_label(status, mode, server_label),
    tone: TONES[status] || 'gray',
    reason: '',
    // The specific reason beats the general meaning of the status.
    note: str(failure_reason) || help,
    note_warn: WARN_STATUSES.has(status)
  };
};

// "Sent by hand by Priya · again of email #12 · Test mode": only what the row carries.
export const origin_line = (r, mode) => {
  const d = r || {};
  const manual = (str(d.send_trigger) || str(d.trigger)) === 'MANUAL';
  const by = str(d.actor_name);
  const parts = [
    manual ? `Sent by hand${by ? ` by ${by}` : ''}` : 'Automatic',
    typeof d.resend_of === 'number' && d.resend_of > 0 ? `again of email #${d.resend_of}` : '',
    mode === 'LIVE' ? 'Live mode' : mode === 'TEST' ? 'Test mode' : ''
  ];
  return parts.filter(Boolean).join(' · ');
};

// GET /email/outbox: newest first; next_before_id 0 = no more rows.
export const outbox_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => {
    const status = str(r.status);
    const mode = str(r.delivery_mode);
    const shown = status_display({
      status,
      mode,
      server_label: str(r.status_label),
      failure_reason: str(r.failure_reason),
      delivery_problem: str(r.delivery_problem)
    });
    return {
      id: typeof r.id === 'number' ? r.id : 0,
      uuid: str(r.uuid),
      kind_label: str(r.kind_label) || str(r.kind) || EM_DASH,
      teacher_uuid: str(r.teacher_uuid),
      teacher_name: str(r.teacher_name) || EM_DASH,
      pay_month: str(r.pay_month),
      status,
      status_label: shown.label,
      status_tone: shown.tone,
      status_help: status_help(status, mode),
      status_reason: shown.reason,
      status_note: shown.note,
      status_note_warn: shown.note_warn,
      is_test: mode === 'TEST',
      attempts: typeof r.attempts === 'number' ? r.attempts : 0,
      to: list(r.to),
      cc: list(r.cc),
      subject: str(r.subject),
      failure_reason: str(r.failure_reason),
      delivery_problem: str(r.delivery_problem),
      created_at: with_ist(str(r.created_at_ist)) || EM_DASH,
      next_attempt_at: with_ist(str(r.next_attempt_at_ist)),
      sent_at: with_ist(str(r.sent_at_ist)),
      // A-52: who started it (MANUAL = sent by hand), which email it repeats, and where it went.
      trigger: str(r.send_trigger) || str(r.trigger) || 'AUTO',
      resend_of: typeof r.resend_of === 'number' && r.resend_of > 0 ? r.resend_of : 0,
      mode: mode === 'LIVE' ? 'LIVE' : mode === 'TEST' ? 'TEST' : '',
      origin: origin_line(r, mode)
    };
  });
  const next =
    typeof d.next_before_id === 'number' && d.next_before_id > 0 ? d.next_before_id : null;
  return { rows, next_before_id: next };
};

const on_off = (on) => (on ? 'ON' : 'OFF');

// GET/POST /email/switch. null until a read lands (or when it failed).
export const email_switch_view = (data) => {
  if (!data || typeof data.live_enabled !== 'boolean') return null;
  const inboxes = list(data.test_inboxes);
  const test_to = inboxes.length ? inboxes.join(' and ') : 'the test inboxes';
  const live = str(data.delivery_mode) === 'LIVE';
  let effect;
  if (live) {
    effect = `Payment emails go to each teacher, with a copy to ${str(data.payments_inbox) || 'the payments inbox'}.`;
  } else if (data.live_ignored === true) {
    effect = `The switch is ON, but this is not production, so payment emails still go only to ${test_to}.`;
  } else {
    effect = `Payment emails go only to ${test_to}. Teachers get nothing.`;
  }
  const last = data.last_change;
  const changed =
    last && last.by_name
      ? `Turned ${on_off(last.to)} by ${last.by_name} on ${with_ist(str(last.at_ist))}. Reason: ${last.reason}`
      : '';
  return {
    enabled: data.live_enabled,
    live,
    can_toggle: data.can_toggle === true,
    label: `Send payment emails to teachers: ${on_off(data.live_enabled)}`,
    // The card shows the label as a title and an ON/OFF chip.
    title: 'Send payment emails to teachers',
    state_text: on_off(data.live_enabled),
    effect,
    last_change: changed
  };
};

export const switch_confirm_text = (target) =>
  target
    ? 'Turning this ON sends every new payment email to the teacher, with a copy to the payments inbox. Emails already sent to the test inboxes are not sent again.'
    : 'Turning this OFF sends every new payment email only to the test inboxes. Teachers get nothing until it is back ON.';

export const switch_confirm_label = (target) => `Turn sending to teachers ${on_off(target)}`;

export const switch_reason_error = reason_error;

// A failed switch POST, as the confirm panel shows it. reload: the switch moved meanwhile; re-read it.
export const switch_error = (e) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (code === CODE_SWITCH_CHANGED) {
    return {
      text: message || 'Sending to teachers was switched by someone else. Check it and try again.',
      reload: true
    };
  }
  if (status === 403) {
    return {
      text:
        message || 'Only a Teacher Payment super user can switch sending to teachers on or off.',
      reload: false
    };
  }
  // No answer or a 5xx: the flip may have committed, so say so and re-read the switch.
  if (!status || status >= 500) {
    return {
      text: 'No clear answer from the server: sending to teachers may have changed. It is being re-read; check it before trying again.',
      reload: true
    };
  }
  return {
    text: message || 'Could not change the switch. Nothing was changed. Try again.',
    reload: false
  };
};

// A failed read: a 401/403 goes to the shell (no grant at all); anything else is shown in the tab.
export const read_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (status === 401 || status === 403 || code === 'FORBIDDEN') return { kind: 'permission' };
  return { kind: 'inline', text: message || fallback };
};

export const outbox_filters_on = (f) => !!(f && (f.teacher_uuid || f.status || f.kind));

const teacher_part = (uuid, name) => {
  if (!uuid) return 'every teacher';
  return name ? `teacher ${name}` : 'one teacher';
};
const label_of = (list, v) => (list.find((o) => o.value === v) || {}).label || v;

// The line under the email filters, every filter named (defaults spelled out); null until loaded.
export const outbox_filter_summary = ({ list, filters, teacher_name }) => {
  if (!list || list.state !== 'ready') return null;
  const n = list.rows.length;
  const count = `${n}${list.next ? '+' : ''}`;
  const noun = n === 1 && !list.next ? 'email' : 'emails';
  const f = filters || {};
  if (!outbox_filters_on(f)) {
    return {
      lead: `${count} ${noun}`,
      rest: ', newest first: every teacher, any status, every email type.'
    };
  }
  const parts = [
    teacher_part(f.teacher_uuid, teacher_name),
    f.status ? label_of(OUTBOX_STATUSES, f.status) : 'any status',
    f.kind ? label_of(OUTBOX_KINDS, f.kind) : 'every email type'
  ];
  return { lead: `${count} ${noun}`, rest: `, newest first: ${parts.join(', ')}.` };
};
